import React, { useState, useMemo } from 'react';
import { Badge, Spinner, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faShieldAlt, faCube, faChevronDown } from '@fortawesome/free-solid-svg-icons';

const groupByModuleAndResource = (capabilities) => {
  const groups = {};
  capabilities.forEach(cap => {
    const mod = cap.moduleKey || 'General';
    const rt = cap.targetResourceType || 'Other';
    if (!groups[mod]) groups[mod] = {};
    if (!groups[mod][rt]) groups[mod][rt] = [];
    groups[mod][rt].push(cap);
  });
  return groups;
};

// ─── Collapsible RT subgroup (read-only) ─────────────────────────────────────
const RtCapGroup = ({ rt, caps }) => {
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div className="cap-rt-subgroup">
      <div className="cap-rt-subheader" onClick={() => setCollapsed(p => !p)}>
        <div className="rt-header-left">
          <FontAwesomeIcon icon={faChevronDown} className={`rt-collapse-icon ${collapsed ? 'collapsed' : ''}`} />
          <FontAwesomeIcon icon={faCube} className="cap-rt-subicon" />
          <span>{rt}</span>
          <Badge className="rt-count-badge">{caps.length}</Badge>
        </div>
      </div>
      {!collapsed && (
        <div className="caps-rt-list">
          {caps.map(cap => (
            <div key={cap.id} className="cap-list-item">
              <div className="cap-list-icon">
                <FontAwesomeIcon icon={faShieldAlt} />
              </div>
              <div className="cap-list-body">
                <strong className="cap-name">{cap.name}</strong>
                {cap.description && <span className="cap-desc">{cap.description}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Collapsible Module group (read-only) ────────────────────────────────────
const ModuleGroup = ({ mod, resourceTypes }) => {
  const [collapsed, setCollapsed] = useState(true);
  const total = Object.values(resourceTypes).flat().length;

  return (
    <div className="module-group">
      <div className="module-group-header" onClick={() => setCollapsed(p => !p)}>
        <div className="module-header-left">
          <FontAwesomeIcon icon={faChevronDown} className={`rt-collapse-icon ${collapsed ? 'collapsed' : ''}`} />
          <span className="module-header-name">{mod}</span>
          <Badge className="auth-badge-role module-count-badge">{total}</Badge>
        </div>
      </div>
      {!collapsed && (
        <div className="module-group-body">
          {Object.entries(resourceTypes).sort(([a], [b]) => a.localeCompare(b)).map(([rt, caps]) => (
            <RtCapGroup key={rt} rt={rt} caps={caps} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const CapabilitiesList = ({ capabilities, isLoading }) => {
  const [search, setSearch] = useState('');
  const [selectedModule, setSelectedModule] = useState('');

  const allModules = useMemo(() =>
    [...new Set(capabilities.map(c => c.moduleKey).filter(Boolean))].sort(),
    [capabilities]
  );

  const grouped = useMemo(() => {
    const filtered = capabilities.filter(c => {
      const matchSearch = !search ||
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        (c.targetResourceType || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.description || '').toLowerCase().includes(search.toLowerCase());
      const matchModule = !selectedModule || c.moduleKey === selectedModule;
      return matchSearch && matchModule;
    });
    return groupByModuleAndResource(filtered);
  }, [capabilities, search, selectedModule]);

  const moduleKeys = Object.keys(grouped).sort();

  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spinner animation="border" style={{ color: '#3f88a5' }} />
        <p>Loading capabilities...</p>
      </div>
    );
  }

  return (
    <div className="capabilities-list-tab">
      <div className="auth-tab-header">
        <div>
          <h4>Capabilities</h4>
          <p className="text-muted mb-0">{capabilities.length} total capabilities</p>
        </div>
      </div>

      <div className="auth-filter-bar">
        <div className="auth-search-bar">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <Form.Control
            type="text"
            placeholder="Search by name or resource type..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="auth-search-input"
          />
        </div>
        <Form.Select
          value={selectedModule}
          onChange={e => setSelectedModule(e.target.value)}
          className="auth-module-filter"
        >
          <option value="">All Modules</option>
          {allModules.map(m => <option key={m} value={m}>{m}</option>)}
        </Form.Select>
      </div>

      <div className="module-groups-list">
        {moduleKeys.map(mod => (
          <ModuleGroup key={mod} mod={mod} resourceTypes={grouped[mod]} />
        ))}
        {moduleKeys.length === 0 && (
          <div className="auth-empty">
            <FontAwesomeIcon icon={faShieldAlt} className="auth-empty-icon" />
            <p>No capabilities found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CapabilitiesList;